import React, { useState, useCallback } from "react"
import { Modal, Button, Select, notification } from "antd"
import { API, Endpoints } from "utils/api"
import Strings from "utils/strings"
import { OrderExternalDelivery, Route, RouteExternalDelivery } from "../types"

const EXTERNAL_DELIVERY_PARTNERS = [{ value: "uber", label: "Uber" }] as const

type ExternalDeliveryPartner =
	(typeof EXTERNAL_DELIVERY_PARTNERS)[number]["value"]

export function getExternalDeliveryPartner(
	partner: string | null | undefined
): ExternalDeliveryPartner | null {
	const match = EXTERNAL_DELIVERY_PARTNERS.find(
		({ value }) => value === partner
	)
	return match?.value ?? null
}

function getExternalDeliveryPartnerLabel(
	partner: string | null | undefined
): string {
	return (
		EXTERNAL_DELIVERY_PARTNERS.find(({ value }) => value === partner)?.label ||
		partner ||
		""
	)
}

type ErrorDetail =
	| string
	| {
			status?: number
			code?: string
			message?:
				| string
				| { en?: string; pt?: string; [lang: string]: string | undefined }
			en?: string
			pt?: string
			[lang: string]: unknown
	  }

interface ExternalDeliveryApiResults {
	routesExternalDelivery?: Array<{
		routeId: string
		externalDelivery: RouteExternalDelivery
	}>
	ordersExternalDelivery?: Array<{
		orderId: string
		externalDelivery: OrderExternalDelivery
	}>
	failedRoutes?: Array<{
		routeId: string
		reason: ErrorDetail
		error?: ErrorDetail
	}>
}

export function isRouteEligibleForExternalDelivery(
	route: Route,
	referenceDate = new Date()
): boolean {
	const routeDate = new Date(route.date)
	if (Number.isNaN(routeDate.getTime())) return false

	return (
		route.status === "dispatching" &&
		!route.externalDelivery?.partner &&
		route.routeType === "bike" &&
		routeDate.setHours(0, 0, 0, 0) ===
			new Date(referenceDate).setHours(0, 0, 0, 0)
	)
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

interface UseExternalDeliveryAssignOptions {
	routeIds: string[]
	setRoutes: React.Dispatch<React.SetStateAction<Route[]>>
}

export interface FailedRouteInfo {
	routeId: string
	routeName: string
	reason: ErrorDetail
	error?: ErrorDetail
}

export interface UseExternalDeliveryAssignReturn {
	selectedPartner: ExternalDeliveryPartner | null
	setSelectedPartner: React.Dispatch<
		React.SetStateAction<ExternalDeliveryPartner | null>
	>
	showModal: boolean
	setShowModal: React.Dispatch<React.SetStateAction<boolean>>
	submitting: boolean
	handleSubmit: () => Promise<void>
	failedRoutes: FailedRouteInfo[]
	clearFailedRoutes: () => void
}

/**
 * Encapsulates the external-delivery assignment flow shared by
 * RouteDetailPanel (single route) and MultiRoutePanel (multiple routes).
 *
 * Responsibilities:
 * - State for the partner picker and confirm modal
 * - POST to /routes/external-delivery with the supplied routeIds
 * - Merge the API response into the shared routes state
 * - Show success/error notifications; surface per-route failures from 207
 *   responses via the `failedRoutes` state so callers can render a modal
 */
export function useExternalDeliveryAssign({
	routeIds,
	setRoutes,
}: UseExternalDeliveryAssignOptions): UseExternalDeliveryAssignReturn {
	const [selectedPartner, setSelectedPartner] =
		useState<ExternalDeliveryPartner | null>(null)
	const [showModal, setShowModal] = useState(false)
	const [submitting, setSubmitting] = useState(false)
	const [failedRoutes, setFailedRoutes] = useState<FailedRouteInfo[]>([])
	const clearFailedRoutes = useCallback(() => setFailedRoutes([]), [])

	const handleSubmit = useCallback(async () => {
		if (routeIds.length === 0 || !selectedPartner) return

		setSubmitting(true)
		try {
			const response = await API.post({
				url: Endpoints.v2.uriRoutes({ routeId: "external-delivery" }),
				data: { routeIds, partner: selectedPartner },
			})

			if (response.ok || response.status === 207) {
				const results: ExternalDeliveryApiResults =
					response?.data?.results || {}
				const routesExternalDelivery = results.routesExternalDelivery || []
				const ordersExternalDelivery = results.ordersExternalDelivery || []
				const apiFailedRoutes = results.failedRoutes || []

				const edByRoute = new Map(
					routesExternalDelivery.map((r) => [r.routeId, r.externalDelivery])
				)
				const edByOrder = new Map(
					ordersExternalDelivery.map((o) => [o.orderId, o.externalDelivery])
				)
				const assignedIds = new Set(routeIds)

				const nameById = new Map<string, string>()
				setRoutes((prev) => {
					prev.forEach((r) => nameById.set(r._id, r.name))
					return prev.map((r) => {
						if (!assignedIds.has(r._id)) return r
						const updatedEd = edByRoute.get(r._id)
						return {
							...r,
							...(updatedEd
								? {
										externalDelivery: updatedEd,
										// Now handed off to a partner — reflect the grey
										// "assigned" state in the eligibility column.
										externalEligibility: {
											...r.externalEligibility,
											status: "assigned" as const,
											partner: updatedEd.partner ?? null,
											reason: null,
											warningOrders: [],
										},
									}
								: {}),
							addresses: r.addresses.map((addr) => {
								const oid = addr.order?.orderId
								if (!oid) return addr
								const updated = edByOrder.get(oid)
								if (!updated) return addr
								return {
									...addr,
									order: { ...addr.order, externalDelivery: updated },
								}
							}),
						}
					})
				})

				if (apiFailedRoutes.length > 0) {
					setFailedRoutes(
						apiFailedRoutes.map((f) => ({
							routeId: f.routeId,
							routeName: nameById.get(f.routeId) || f.routeId,
							reason: f.reason,
							error: f.error,
						}))
					)
				} else {
					notification.success({
						message: Strings.routes.assignToExternal,
						description: Strings.routes.externalDeliverySuccess,
						placement: "bottomRight",
						duration: 3,
					})
				}
			} else {
				const code = response?.data?.code
				notification.error({
					message: Strings.routes.updateFailed,
					description:
						code === "EXTERNAL_DELIVERY_PENDING_BATCHES"
							? Strings.routes.externalDeliveryPendingBatches
							: response?.data?.message || Strings.routes.externalDeliveryError,
					placement: "bottomRight",
					duration: 5,
				})
			}
		} catch {
			notification.error({
				message: Strings.routes.updateFailed,
				description: Strings.routes.externalDeliveryError,
				placement: "bottomRight",
				duration: 5,
			})
		} finally {
			setSubmitting(false)
			setSelectedPartner(null)
			setShowModal(false)
		}
	}, [routeIds, selectedPartner, setRoutes])

	return {
		selectedPartner,
		setSelectedPartner,
		showModal,
		setShowModal,
		submitting,
		handleSubmit,
		failedRoutes,
		clearFailedRoutes,
	}
}

// ---------------------------------------------------------------------------
// Partner picker Select
// ---------------------------------------------------------------------------

interface ExternalDeliverySelectProps {
	value: ExternalDeliveryPartner | null
	onChange: (val: ExternalDeliveryPartner | null) => void
	disabled?: boolean
	placeholder?: string
	style?: React.CSSProperties
}

/**
 * The partner-picker Select shared between panels. Triggers the confirm
 * modal when a partner is chosen; clears on the × button.
 */
export const ExternalDeliverySelect: React.FC<ExternalDeliverySelectProps> = ({
	value,
	onChange,
	disabled,
	placeholder,
	style,
}) => (
	<Select
		value={value ?? undefined}
		onChange={(val) => onChange(getExternalDeliveryPartner(String(val)))}
		allowClear
		onClear={() => onChange(null)}
		disabled={disabled}
		style={style ?? { width: "100%" }}
		placeholder={placeholder ?? Strings.routes.selectExternalProvider}
		options={EXTERNAL_DELIVERY_PARTNERS.map(({ value, label }) => ({
			value,
			label,
		}))}
	/>
)

// ---------------------------------------------------------------------------
// Assignment modal — handles three modes:
//   1. Confirm (default): "Are you sure?" + route/order summary
//   2. All untreated: notice with only a Close button
//   3. Failed routes: list of per-route errors from a 207 partial success
// ---------------------------------------------------------------------------

interface ExternalDeliveryModalProps {
	visible: boolean
	partner: ExternalDeliveryPartner | null
	submitting: boolean
	onConfirm: () => void
	onCancel: () => void
	/** Number of routes being assigned — used to build the summary line. */
	routeCount: number
	/** Number of orders being assigned — used to build the summary line. */
	orderCount: number
	/** When true, render the "all orders untreated" notice (no confirm action). */
	allUntreated?: boolean
	/** When non-empty, the modal switches to error mode listing per-route failures. */
	failedRoutes?: FailedRouteInfo[]
	/** Called when the user dismisses the failed-routes view. */
	onCloseFailedRoutes?: () => void
}

function stringifyDetail(value: unknown): string {
	if (value == null) return ""
	if (typeof value === "string") return value
	if (typeof value !== "object") return String(value)
	const obj = value as Record<string, unknown>
	const lang = Strings.getLanguage?.()
	if (lang && typeof obj[lang] === "string") return obj[lang] as string
	if (typeof obj.en === "string") return obj.en
	if (typeof obj.pt === "string") return obj.pt
	if (obj.message !== undefined) return stringifyDetail(obj.message)
	if (typeof obj.code === "string") return obj.code
	return ""
}

export const ExternalDeliveryModal: React.FC<ExternalDeliveryModalProps> = ({
	visible,
	partner,
	submitting,
	onConfirm,
	onCancel,
	routeCount,
	orderCount,
	allUntreated = false,
	failedRoutes = [],
	onCloseFailedRoutes,
}) => {
	const partnerLabel = getExternalDeliveryPartnerLabel(partner)
	const hasFailures = failedRoutes.length > 0
	const modalVisible = visible || hasFailures

	const handleCancel = () => {
		if (hasFailures && onCloseFailedRoutes) onCloseFailedRoutes()
		else onCancel()
	}

	const title = hasFailures
		? Strings.routes.externalDeliveryFailedTitle
		: Strings.routes.assignToExternal

	let footer: React.ReactNode
	if (hasFailures) {
		footer = [
			<Button key="close" type="primary" onClick={handleCancel}>
				{Strings.generic.close}
			</Button>,
		]
	} else if (allUntreated) {
		footer = [
			<Button key="close" onClick={handleCancel}>
				{Strings.generic.close}
			</Button>,
		]
	} else {
		footer = [
			<Button key="cancel" onClick={handleCancel} disabled={submitting}>
				{Strings.generic.cancel}
			</Button>,
			<Button
				key="confirm"
				type="primary"
				danger
				loading={submitting}
				onClick={onConfirm}
			>
				{Strings.routes.assignToExternal}
			</Button>,
		]
	}

	return (
		<Modal
			title={title}
			visible={modalVisible}
			onCancel={handleCancel}
			footer={footer}
		>
			{hasFailures ? (
				<>
					<p>{Strings.routes.externalDeliveryFailedDescription}</p>
					<ul style={{ paddingLeft: 20, margin: 0 }}>
						{failedRoutes.map((f) => {
							const reasonText = stringifyDetail(f.reason)
							const errorText = stringifyDetail(f.error)
							return (
								<li key={f.routeId} style={{ marginBottom: 8 }}>
									<strong>{f.routeName}</strong>
									{reasonText && <div>{reasonText}</div>}
									{errorText && (
										<div style={{ color: "#a8071a", fontSize: 12 }}>
											{errorText}
										</div>
									)}
								</li>
							)
						})}
					</ul>
				</>
			) : allUntreated ? (
				<p>{Strings.routes.allOrdersUntreated}</p>
			) : (
				<>
					<p>
						{Strings.routes.externalDeliveryConfirm.replace(
							"{0}",
							partnerLabel
						)}
					</p>
					{orderCount > 0 && (
						<p>
							{Strings.formatString(
								Strings.routes.externalDeliveryMultiSummary,
								routeCount,
								orderCount
							)}
						</p>
					)}
				</>
			)}
		</Modal>
	)
}
